import React, { PropsWithChildren, useMemo } from 'react';

import { useLocation } from 'react-router-dom';

import Layout from './index';
import LayoutMobile from './LayoutMobile';
import { PublicLayout } from './PublicLayout';
import { logger } from 'utils/logger';
import { useIsMobile } from 'utils/mediaQueries';

/**
 * Static routes that use PublicLayout (no authentication required)
 */
const PUBLIC_STATIC_ROUTES = [
  '/login',
  '/register',
  '/forgot-password',
  '/register-iframe',
  '/logout',
];

/**
 * Dynamic route patterns that use PublicLayout
 * These routes have parameters and need pattern matching
 */
const PUBLIC_DYNAMIC_PATTERNS = [
  /^\/invite\/event\/[^/]+$/, // /invite/event/:invite_uuid
  /^\/form\/[^/]+$/, // /form/:formGroupId
];

/**
 * Check if the current path is a public route
 */
const isPublicRoutePath = (pathname: string): boolean => {
  // Check static routes
  if (PUBLIC_STATIC_ROUTES.includes(pathname)) {
    return true;
  }

  // Check dynamic patterns
  return PUBLIC_DYNAMIC_PATTERNS.some(pattern => pattern.test(pathname));
};

const LayoutSwitcher: React.FC<PropsWithChildren<{}>> = ({ children }) => {
  const isMobile = useIsMobile();
  const location = useLocation();

  const isPublicRoute = useMemo(() => isPublicRoutePath(location.pathname), [location.pathname]);

  logger.debug('[LayoutSwitcher] Estado atual:', {
    isMobile,
    currentPath: location.pathname,
    isPublicRoute,
  });

  // Public routes use PublicLayout (whitelabel: logo, background, theme)
  if (isPublicRoute) {
    logger.debug('[LayoutSwitcher] Public route, applying PublicLayout');
    return <PublicLayout centered={false}>{children}</PublicLayout>;
  }

  // Private routes use full layout (with menu, sidebar, etc.)
  logger.debug('[LayoutSwitcher] Private route, applying layout:', isMobile ? 'Mobile' : 'Desktop');
  return isMobile ? <LayoutMobile>{children}</LayoutMobile> : <Layout>{children}</Layout>;
};

export default LayoutSwitcher;
